import { useState, type FormEvent, type JSX } from 'react';
import type { TakiConfig } from '@taki/runtime-config';
import { signInWithEmail } from '@taki/auth';
import { Button, Card, Input } from '@taki/ui';
import { TakiWordmark } from './TakiWordmark.js';

export interface LoginScreenProps {
  config: TakiConfig;
}

/**
 * Connexion email / mot de passe affichee par l'auth guard quand aucun
 * utilisateur n'est connecte. Restylee aux vrais tokens Taki : page bg-bg,
 * carte centree (bg-surface, shadow-panel), CTA lime. En v0 seul email +
 * mot de passe est branche (chemin de test de `@taki/auth`).
 */
export function LoginScreen({ config }: LoginScreenProps): JSX.Element {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await signInWithEmail(email, password);
      // En cas de succes, `useUser()` bascule en etat connecte et le guard
      // remplace cet ecran par le shell automatiquement ; rien d'autre a faire.
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Echec de la connexion.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-bg p-6 text-ink-900">
      <Card className="w-full max-w-sm p-8 shadow-panel">
        <div className="mb-7 flex flex-col items-center gap-3 text-center">
          {config.branding.logoUrl ? (
            <img
              src={config.branding.logoUrl}
              alt={config.branding.name}
              className="h-9 w-auto"
            />
          ) : (
            <span className="text-lg font-semibold text-ink-900">
              {config.branding.name}
            </span>
          )}
          <p className="text-sm text-ink-500">Connexion a votre espace</p>
        </div>

        <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium text-ink-700">Email</span>
            <Input
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="vous@exemple.com"
            />
          </label>

          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium text-ink-700">Mot de passe</span>
            <Input
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="••••••••"
            />
          </label>

          {error ? (
            <p role="alert" className="text-sm text-danger">
              {error}
            </p>
          ) : null}

          <Button type="submit" size="lg" className="w-full" disabled={submitting}>
            {submitting ? 'Connexion…' : 'Se connecter'}
          </Button>
        </form>

        <div className="mt-7 flex items-center justify-center gap-2 opacity-50">
          <span className="text-[0.625rem] tracking-[0.04em] text-ink-500">powered by</span>
          <TakiWordmark height={16} className="text-ink-500" />
        </div>
      </Card>
    </div>
  );
}
